<template>
  <nav v-if="hasBreadcrumbLinks" aria-label="breadcrumbs" class="breadcrumbs-bar show-on-load">
    <ol class="breadcrumbs-bar__container container">
      <li
        v-for="(link, index) in fields.links"
        :key="link?.href"
        class="breadcrumbs-bar__breadcrumb"
      >
        <component
          :is="isLastLink(index) ? 'span' : getLinkTag(getBreadcrumbLinkKind(link), link?.href)"
          v-bind="getBreadcrumbLinkProps(link, index)"
          :data-test-id="
            isLastLink(index) ? 'breadcrumb-current' : `breadcrumb-${getBreadcrumbLinkKind(link)}`
          "
          :aria-current="isLastLink(index) ? 'page' : null"
          class="breadcrumbs-bar__breadcrumb-el"
          :class="isLastLink(index) ? 'breadcrumbs-bar__breadcrumb-el--text' : 'inverted-underline'"
          @click="isLastLink(index) ? null : handleBreadcrumbClick(link)"
        >
          {{ link?.title }}
        </component>
        <i
          v-if="!isLastLink(index)"
          class="icon icon-Forward breadcrumbs-bar__breadcrumb-icon"
          aria-hidden="true"
        />
      </li>
    </ol>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useButtonsAndLinksTracking } from '../../data-layer/hooks/useButtonsAndLinksTracking';
import { getLinkTrackingData } from '../../data-layer/helpers/utils';
import { getLinkTag, getLinkBindings } from '../../utils/linkTransformer';
import type { BreadcrumbsBarProps, BreadcrumbLink } from './types';

const props = withDefaults(defineProps<BreadcrumbsBarProps>(), {
  fields: () => ({}),
  params: () => ({}),
});

const hasBreadcrumbLinks = computed(() => props.fields.links?.length > 0);

const isLastLink = (index: number) => index === props.fields.links?.length - 1;

const getBreadcrumbLinkKind = (link: BreadcrumbLink) =>
  link?.isInternalLink ? 'internal' : 'external';

const getBreadcrumbLinkProps = (link: BreadcrumbLink, index: number) => {
  if (isLastLink(index) || !link?.href) return {};

  return getLinkBindings({
    value: {
      href: link.href,
      linktype: getBreadcrumbLinkKind(link),
    },
  });
};

const { trackLinkState } = useButtonsAndLinksTracking({
  componentName: 'breadcrumb_navigation',
});

const handleBreadcrumbClick = (link: BreadcrumbLink): void => {
  if (!link?.href || !link?.title) return;

  const { link_page_path, link_domain } = getLinkTrackingData(link.href);
  const isExternalLink = !link?.isInternalLink;

  trackLinkState(link.title, 'navigation_interaction', {
    link_text: link.title,
    link_page_path,
    ...(isExternalLink ? { link_domain } : {}),
  });
};
</script>

<style lang="scss">
@import './scss/BreadcrumbsBar.scss';
</style>
